import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { STATUS_DICTIONARY } from "constants";
import { AutoComplete, Button, Input, Select } from "antd";
import { DatePicker as DatePickerJalali } from "antd-jalali";
import dayjs from "dayjs";
import { useQuery } from "react-query";
import { useSearchDebounce } from "utils/useDebouncedSearch";
import { getUsers } from "services/user";
import { GetVendors } from "services/vendors";

function OrderFilters() {
  const colorPalette = useSelector(({ theme }) => theme.palette);

  const [params] = useSearchParams();

  const [startAt, setStartAt] = useState(params.get("start_date"));
  const [endAt, setEndAt] = useState(params.get("end_date"));
  const [status, setStatus] = useState(params.get("status"));
  const [selectedVendor, setSelectedVendor] = useState(params.get("vendor"));
  const [selectedUser, setSelectedUser] = useState(params.get("user"));

  const [selectedVendorLabel, setSelectedVendorLabel] = useState(
    params.get("vendor_label"),
  );
  const [selectedUserLabel, setSelectedUserLabel] = useState(
    params.get("user_label"),
  );

  const { state } = useLocation();
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();

    const queryObj = {
      vendor: selectedVendor,
      vendor_label: selectedVendorLabel,
      user: selectedUser,
      user_label: selectedUserLabel,
      status: status,
      start_date: startAt,
      end_date: endAt,
      page: 1,
    };

    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(queryObj).filter(([_, v]) => v)),
    );

    navigate({
      search: query.toString(),
    });
  };

  const handleClearFilters = () => {
    navigate({ search: "" });

    setSelectedUser(null);
    setSelectedVendor(null);
    setStatus(null);
    setStartAt(null);
    setEndAt(null);

    setSelectedUserLabel(null);
    setSelectedVendorLabel(null);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="pr_table_header"
      style={{
        borderTop: "1px solid " + colorPalette.text.op_8,
        borderBottom: "1px solid " + colorPalette.text.op_8,
        display: "flex",
        gap: "0.5rem",
      }}
    >
      <CustomerSearch
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        selectedUserLabel={selectedUserLabel}
        setSelectedUserLabel={setSelectedUserLabel}
      />

      <VendorSearch
        selectedVendor={selectedVendor}
        setSelectedVendor={setSelectedVendor}
        selectedVendorLabel={selectedVendorLabel}
        setSelectedVendorLabel={setSelectedVendorLabel}
      />

      <DatePickerJalali.RangePicker
        defaultValue={startAt && endAt ? [dayjs(startAt), dayjs(endAt)] : null}
        onCalendarChange={(dates) => {
          if (!dates) {
            setStartAt(null);
            setEndAt(null);
          } else {
            setStartAt(new Date(dates[0]).toISOString().split("T")[0]);
            setEndAt(new Date(dates[1]).toISOString().split("T")[0]);
          }
        }}
      />

      <Select
        value={status}
        placeholder="وضعیت"
        onChange={(val) => setStatus(val)}
        style={{ borderRadius: 8, width: 200 }}
        options={Object.keys(STATUS_DICTIONARY).map((d) => ({
          value: d,
          label: STATUS_DICTIONARY[d].label,
        }))}
      />

      <Button
        htmlType="submit"
        type="primary"
        style={{ marginRight: "auto", borderRadius: 4 }}
      >
        اعمال فیلترها
      </Button>

      <Button type="text" danger onClick={handleClearFilters}>
        حذف تمامی فیلتر ها
      </Button>
    </form>
  );
}

export default OrderFilters;

function CustomerSearch({
  selectedUserLabel,
  setSelectedUserLabel,
  setSelectedUser,
}) {
  const [search, setSearch] = useSearchDebounce("");

  const { data } = useQuery(
    ["user-search", search],
    () => getUsers([], { search: search }),
    {
      enabled: Boolean(search.length > 1),
      refetchOnWindowFocus: false,
    },
  );

  const options = useMemo(() => {
    return data?.results.map((item) => ({
      value: item.full_name,
      label: <span>{item.full_name}</span>,
      entry: item,
    }));
  }, [data]);

  return (
    <AutoComplete
      allowClear
      showSearch={false}
      autoClearSearchValue
      options={options}
      style={{ borderRadius: 8 }}
      defaultValue={selectedUserLabel}
      onSelect={(_, obj) => {
        setSelectedUser(obj.entry.id);
        setSelectedUserLabel(obj.entry.full_name);
      }}
      onSearch={(value) => setSearch(value)}
      onClear={() => {
        setSelectedUser(null);
        setSelectedUserLabel(null);
      }}
    >
      <Input size="large" placeholder="جست و جو در کاربران..." />
    </AutoComplete>
  );
}

function VendorSearch({
  selectedVendorLabel,
  setSelectedVendorLabel,
  setSelectedVendor,
}) {
  const [search, setSearch] = useSearchDebounce("");

  const { data } = useQuery(
    ["vendor-search", search],
    () => GetVendors({ search: search }),
    {
      enabled: Boolean(search.length > 1),
      refetchOnWindowFocus: false,
    },
  );

  const options = useMemo(() => {
    return data?.results.map((item) => ({
      value: item.name,
      label: <span>{item.name}</span>,
      entry: item,
    }));
  }, [data]);

  return (
    <AutoComplete
      allowClear
      showSearch={false}
      autoClearSearchValue
      style={{ borderRadius: 8 }}
      options={options}
      defaultValue={selectedVendorLabel}
      onSelect={(_, obj) => {
        setSelectedVendor(obj.entry.id);
        setSelectedVendorLabel(obj.entry.name);
      }}
      onSearch={(value) => setSearch(value)}
      onClear={() => {
        setSelectedVendor(null);
        setSelectedVendorLabel(null);
      }}
    >
      <Input size="large" placeholder="جست و جو در فروشگاه..." />
    </AutoComplete>
  );
}
